import React, { useState, useEffect, useCallback, useContext } from 'react';
import Modal from '../../modal/Modal';
import styles from '../admin-dashboard.module.scss';
import { PractitionerContext } from '../../../contexts/Practitioner';
import { updateOrganizationScoreComponents } from '../../../services/api/organization';
import { ScoreComponent, ScoreComponentName } from '../../../interfaces/organization';

type Props = {
  show: boolean;
  hide: () => void;
  selectedMetrics: Record<string, number>;
  organizationId: string;
  onSave: (metrics: string[], sliderValues: Record<string, number>) => void;
};

const ViviHealthMetricsModal: React.FC<Props> = ({ show, hide, selectedMetrics, onSave, organizationId }) => {
  const [tempSelectedMetrics, setTempSelectedMetrics] = useState<Record<string, number>>({});
  const [sliderValues, setSliderValues] = useState<Record<string, number>>({});
  const [userAdjustedSliders, setUserAdjustedSliders] = useState<Set<string>>(new Set());
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Map display names to API names
  const metricNameMap: Record<string, string> = {
    Steps: 'exercise',
    Engagement: 'engagement',
    Stress: 'stress',
    Sleep: 'sleep',
    Activity: 'activity_count',
    Mood: 'mood',
    Adherence: 'adherence',
  };

  console.log('Org id', organizationId);

  const availableMetrics = ['Steps', 'Engagement', 'Stress', 'Sleep', 'Activity', 'Mood', 'Adherence'];

  // Metric descriptions based on the component definitions
  const metricDescriptions: Record<string, string> = {
    Steps: "Patient's average steps across the week",
    Engagement:
      'Average engagement score (meeting attendance, communication, testing completion, survey completion, self-help content utilization, and health band wear time)',
    Stress: 'Average stress score',
    Sleep: 'Average sleep quality score',
    Activity: 'Composite measure of total weekly activity time and spread across each day of the week',
    Mood: 'Average mood score',
    Adherence:
      'Average adherence score (meeting attendance, testing completion, test failures, facial mismatches, and geofence violations)',
  };

  // Initialize state when modal opens
  useEffect(() => {
    if (show) {
      setTempSelectedMetrics(selectedMetrics);
      setSliderValues(selectedMetrics);
      setUserAdjustedSliders(new Set());
    }
  }, [show, selectedMetrics]);

  // Helper function to distribute values among unadjusted sliders
  const distributeRemainingValues = (
    currentValues: Record<string, number>,
    adjustedSliders: Set<string>,
    selectedMetrics: string[],
  ) => {
    const adjustedTotal = Array.from(adjustedSliders).reduce((sum, metric) => sum + (currentValues[metric] || 0), 0);
    const remainingPercentage = Math.max(0, 100 - adjustedTotal);
    const unadjustedMetrics = selectedMetrics.filter(metric => !adjustedSliders.has(metric));

    if (unadjustedMetrics.length === 0) return currentValues;

    const equalValue = Math.floor(remainingPercentage / unadjustedMetrics.length);
    const remainder = remainingPercentage - equalValue * unadjustedMetrics.length;

    const newValues = { ...currentValues };
    unadjustedMetrics.forEach((metric, index) => {
      newValues[metric] = equalValue + (index < remainder ? 1 : 0);
    });

    return newValues;
  };

  const handleCancel = () => {
    hide();
  };

  const handleSave = () => {
    let newComponents: any = [];
    for (const [key, value] of Object.entries(sliderValues)) {
      newComponents.push({ name: key, weight: value });
    }
    updateOrganizationScoreComponents(organizationId || '', newComponents);
    onSave(Object.keys(sliderValues), sliderValues);
    setShowConfirmation(true);
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    hide();
  };

  const handleMetricToggle = (metric: string) => {
    const apiName = metricNameMap[metric];
    setTempSelectedMetrics(prev => {
      const newState = { ...prev };
      if (newState.hasOwnProperty(apiName)) {
        // Remove metric and its adjusted status
        delete newState[apiName];
        setUserAdjustedSliders(prevAdjusted => {
          const newAdjusted = new Set(prevAdjusted);
          newAdjusted.delete(apiName);
          return newAdjusted;
        });

        // Remove from slider values and redistribute
        setSliderValues(prevSlider => {
          const newSliderState = { ...prevSlider };
          delete newSliderState[apiName];

          const remainingMetrics = Object.keys(newState);
          if (remainingMetrics.length > 0) {
            return distributeRemainingValues(newSliderState, new Set(), remainingMetrics);
          }
          return newSliderState;
        });
      } else {
        // Add metric
        newState[apiName] = 0;

        // Distribute values including the new metric
        setSliderValues(prevSlider => {
          const newSliderState = { ...prevSlider, [apiName]: 0 };
          const allSelectedMetrics = Object.keys(newState);
          return distributeRemainingValues(newSliderState, userAdjustedSliders, allSelectedMetrics);
        });
      }
      return newState;
    });
  };

  const handleSliderChange = (metric: string, value: number) => {
    const apiName = metricNameMap[metric];
    setSliderValues(prev => {
      const newValues = { ...prev, [apiName]: value };
      const newAdjustedSliders = new Set(userAdjustedSliders).add(apiName);
      const selectedMetrics = Object.keys(tempSelectedMetrics);
      return distributeRemainingValues(newValues, newAdjustedSliders, selectedMetrics);
    });
    setUserAdjustedSliders(prev => new Set(prev).add(apiName));
  };

  return (
    <>
      <Modal show={show} closeModal={handleCancel} showBtnClose={false} contentStyle='mediumContent60'>
        <div className={styles.metricsModal}>
          <div className={styles.modalHeader}>
            <h3>Select ViviHealth Metrics</h3>
            <p className={styles.distributionNote}>
              Selected metrics start equally distributed (100% total). Adjust any slider to lock its value and
              redistribute the remainder.
            </p>
          </div>

          <div className={styles.metricsList}>
            {availableMetrics.map(metric => {
              const apiName = metricNameMap[metric];
              const isSelected = tempSelectedMetrics.hasOwnProperty(apiName);
              const sliderValue = sliderValues[apiName] || 0;
              const description = metricDescriptions[metric];

              return (
                <div key={metric} className={styles.metricItem}>
                  <label className={styles.metricLabel} htmlFor={`metric-${metric.replace(/\s+/g, '-')}`}>
                    <input
                      type='checkbox'
                      id={`metric-${metric.replace(/\s+/g, '-')}`}
                      checked={isSelected}
                      onChange={() => handleMetricToggle(metric)}
                      className={styles.metricCheckbox}
                    />
                    <div className={styles.metricTextContainer}>
                      <span className={styles.metricText}>{metric}</span>
                      <span className={styles.metricDescription}>{description}</span>
                    </div>
                  </label>
                  {isSelected && (
                    <div className={styles.sliderContainer}>
                      <input
                        type='range'
                        min='0'
                        max='100'
                        value={sliderValue}
                        onChange={e => handleSliderChange(metric, parseInt(e.target.value, 10))}
                        className={`${styles.metricSlider} ${
                          userAdjustedSliders.has(apiName) ? styles.userAdjusted : ''
                        }`}
                        style={{ '--slider-value': `${sliderValue}%` } as React.CSSProperties}
                      />
                      <span
                        className={`${styles.sliderValue} ${
                          userAdjustedSliders.has(apiName) ? styles.userAdjustedValue : ''
                        }`}
                      >
                        {sliderValue}%
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className={styles.modalButtons}>
            <button type='button' className={styles.cancelButton} onClick={handleCancel}>
              Cancel
            </button>
            <button type='button' className={styles.saveButton} onClick={handleSave}>
              Save
            </button>
          </div>
        </div>
      </Modal>
      <ConfirmationModal show={showConfirmation} onClose={handleConfirmationClose} />
    </>
  );
};

// Confirmation Modal Component
const ConfirmationModal: React.FC<{ show: boolean; onClose: () => void }> = ({ show, onClose }) => {
  return (
    <Modal show={show} closeModal={onClose} showBtnClose={false} contentStyle='smallContent'>
      <div className={styles.confirmationModal}>
        <div className={styles.confirmationContent}>
          <div className={styles.confirmationIcon}>✓</div>
          <h3>Settings Saved Successfully</h3>
          <p>Please allow up to an hour for changes to reflect in the ViviHealth score.</p>
          <button type='button' className={styles.confirmationButton} onClick={onClose}>
            OK
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ViviHealthMetricsModal;
